import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiLayers, FiPackage, FiShoppingBag, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import AnimatedCounter from '../components/AnimatedCounter';

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [stats, setStats] = useState({
    totalCategories: 0,
    activeCategories: 0,
    totalProducts: 0,
    averageProductsPerCategory: 0
  });
  const { selectedHierarchy } = useAuth();

  useEffect(() => {
    loadCategories();
  }, [selectedHierarchy]);

  const loadCategories = async () => {
    if (!selectedHierarchy?.outlet?.id) return;
    
    try {
      setLoading(true);
      
      // Set up real-time listener for categories
      const categoriesQuery = query(
        collection(db, 'categories'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      
      const unsubscribeCategories = onSnapshot(categoriesQuery, (snapshot) => {
        const categoriesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCategories(categoriesData);
        
        // Calculate category stats immediately
        const totalCategories = categoriesData.length;
        const activeCategories = categoriesData.filter(cat => cat.active !== false).length;
        
        // Update stats with category data (products will be updated by products listener)
        setStats(prevStats => ({
          ...prevStats,
          totalCategories,
          activeCategories
        }));
      });

      // Load products for stats
      const productsQuery = query(
        collection(db, 'products'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      
      const unsubscribeProducts = onSnapshot(productsQuery, (productsSnapshot) => {
        const productsData = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProducts(productsData);
        
        // Calculate product stats and update
        const totalProducts = productsData.length;
        
        setStats(prevStats => ({
          ...prevStats,
          totalProducts,
          averageProductsPerCategory: prevStats.totalCategories > 0 ? totalProducts / prevStats.totalCategories : 0
        }));
      });

      setLoading(false);

      // Return cleanup function
      return () => {
        unsubscribeCategories();
        unsubscribeProducts();
      };
    } catch (error) {
      console.error('Error loading categories:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // SECURITY: Verify outlet is selected
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    try {
      const categoryData = {
        ...formData,
        outletId: selectedHierarchy.outlet.id,
        updatedAt: new Date()
      };
      
      if (editingCategory) {
        // SECURITY: Verify the category belongs to the current outlet
        if (editingCategory.outletId !== selectedHierarchy.outlet.id) {
          alert('Security Error: Cannot modify category from another outlet.');
          return;
        }
        await updateDoc(doc(db, 'categories', editingCategory.id), categoryData);
      } else {
        categoryData.createdAt = new Date();
        await addDoc(collection(db, 'categories'), categoryData);
      }
      setShowModal(false);
      setFormData({ name: '', description: '' });
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleEdit = (category) => {
    // SECURITY: Verify the category belongs to the current outlet
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    if (category.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot edit category from another outlet.');
      return;
    }
    
    setEditingCategory(category);
    setFormData({ name: category.name, description: category.description || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    // SECURITY: Get the category first to verify ownership
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    const category = categories.find(cat => cat.id === id);
    if (!category) {
      alert('Error: Category not found.');
      return;
    }
    
    if (category.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot delete category from another outlet.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteDoc(doc(db, 'categories', id));
        loadCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const getProductCount = (categoryId) => {
    return products.filter(product => product.categoryId === categoryId).length;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredCategories = categories
    .filter(cat => {
      // Search filter
      const matchesSearch = cat.name?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      
      // Status filter
      if (statusFilter === 'active' && cat.active === false) return false;
      if (statusFilter === 'inactive' && cat.active !== false) return false;
      
      return true;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch(sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'description':
          aValue = (a.description || '').toLowerCase();
          bValue = (b.description || '').toLowerCase();
          break;
        case 'products':
          aValue = getProductCount(a.id);
          bValue = getProductCount(b.id);
          break;
        case 'status':
          aValue = a.active !== false ? 1 : 0;
          bValue = b.active !== false ? 1 : 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Categories</h1>
        <p className="page-subtitle">Manage product categories</p>
      </div>

      {/* Stats Widgets */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#2196F3' }}>
                <FiLayers className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.totalCategories} />
              </div>
              <div className="stat-card-label">Total Categories</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#48bb78' }}>
                <FiShoppingBag className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.activeCategories} />
              </div>
              <div className="stat-card-label">Active Categories</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#00bcd4' }}>
                <FiPackage className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.totalProducts} />
              </div>
              <div className="stat-card-label">Total Products</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#ffa726' }}>
                <FiLayers className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.averageProductsPerCategory} decimals={1} />
              </div>
              <div className="stat-card-label">Avg Products/Category</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card hover-lift">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Categories</h5>
          <button className="btn btn-sm btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus className="me-2" />
            Add Category
          </button>
        </div>
        
        {/* Filter Controls */}
        {!loading && categories.length > 0 && (
          <div className="card-body border-bottom">
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="form-label small text-muted mb-1">Search</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Status</label>
                <select 
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="col-md-3">
                <button
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear Filters
                </button>
              </div>
            </div>
            {(searchTerm || statusFilter !== 'all') && (
              <div className="mt-2">
                <small className="text-muted">
                  Showing {filteredCategories.length} of {categories.length} categories
                </small>
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading categories...</p>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-5">
              <FiLayers size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                {searchTerm || statusFilter !== 'all' 
                  ? 'No categories match your filters.' 
                  : 'No categories found.'}
              </p>
              {(searchTerm || statusFilter !== 'all') && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('name')}
                    >
                      Name
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('description')}
                    >
                      Description
                      {sortField === 'description' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('products')}
                    >
                      Products
                      {sortField === 'products' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('status')}
                    >
                      Status
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map(category => (
                    <tr key={category.id}>
                      <td className="fw-semibold">{category.name}</td>
                      <td>{category.description || '-'}</td>
                      <td>
                        <span className="badge bg-primary">{getProductCount(category.id)}</span>
                      </td>
                      <td>
                        <span className={`badge ${category.active !== false ? 'bg-success' : 'bg-secondary'}`}>
                          {category.active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(category)}
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(category.id)}
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowModal(false)}></div>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingCategory ? 'Edit Category' : 'Add Category'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Category Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      ></textarea>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingCategory ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
